import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '../api/client.js';
import type { CatalogApp } from '../api/types.js';
import { useApp, useDocumentTitle, useMe } from '../app/context.js';
import { useToast } from '../components/toast.js';
import { RoleEditor, Sheet, StatusBadge, dateDe } from '../components/ui.js';

/** Plattform-Administration: Mandanten (nur Gruppe platform-admin mit MFA). */
export function AdminTenantsPage() {
  useDocumentTitle('Administration');
  const { api } = useApp();
  const me = useMe().data!;
  const tenants = useQuery({ queryKey: ['admin', 'tenants'], queryFn: () => api.tenants(), enabled: me.platformAdmin });
  const catalog = useQuery({ queryKey: ['admin', 'catalog'], queryFn: () => api.catalog(), enabled: me.platformAdmin });
  const [creating, setCreating] = useState(false);

  if (!me.platformAdmin) return <Navigate to="/" replace />;
  const appName = (id: string) => catalog.data?.apps.find((a) => a.appId === id)?.name ?? id;

  return (
    <main className="page">
      <header className="page__head">
        <div className="page__head--stack">
          <span className="eyebrow">Administration</span>
          <h1 className="page__title">Mandanten</h1>
        </div>
        <div className="actions">
          <Link to="/admin/katalog" className="btn btn--ghost">Tool-Katalog</Link>
          <button className="btn btn--primary" onClick={() => setCreating(true)}>+ Mandant</button>
        </div>
      </header>

      {tenants.isLoading && <div className="spinner">Lädt …</div>}
      {tenants.error && <div className="alert alert--error">{(tenants.error as Error).message}</div>}

      <section className="list" aria-label="Mandanten">
        {tenants.data?.tenants.map((t) => (
          <Link key={t.tenantId} to={`/admin/mandanten/${t.tenantId}`} className="list__item list__item--link">
            <div className="list__main">
              <strong>{t.name}</strong>
              <span className="muted">
                seit {dateDe(t.createdAt)} · {t.apps.length ? t.apps.map(appName).join(', ') : 'keine Tools'}
              </span>
            </div>
            <div className="list__side">
              <StatusBadge status={t.status} />
            </div>
          </Link>
        ))}
        {tenants.data?.tenants.length === 0 && <div className="empty">Noch keine Mandanten.</div>}
      </section>

      {creating && catalog.data && <CreateTenantSheet catalog={catalog.data.apps} onClose={() => setCreating(false)} />}
    </main>
  );
}

function CreateTenantSheet({ catalog, onClose }: { catalog: CatalogApp[]; onClose: () => void }) {
  const { api } = useApp();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [apps, setApps] = useState<string[]>([]);
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [roles, setRoles] = useState<string[]>(['platform:tenantAdmin']);

  const create = useMutation({
    mutationFn: () =>
      api.createTenant({
        name: name.trim(),
        contactEmail: contactEmail.trim() || undefined,
        apps,
        admin: { name: adminName.trim(), email: adminEmail.trim(), roles: roles.filter((r) => r !== 'platform:tenantAdmin') },
      }),
    onSuccess: async (t) => {
      await qc.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      toast(`${t.name} angelegt – Einladung an ${adminEmail.trim()} verschickt.`);
      navigate(`/admin/mandanten/${t.tenantId}`);
    },
  });

  const email = /^\S+@\S+\.\S+$/;
  const valid = name.trim().length >= 2 && adminName.trim() && email.test(adminEmail.trim()) && (!contactEmail.trim() || email.test(contactEmail.trim()));
  const assignable = catalog.filter((c) => apps.includes(c.appId)).map(({ appId, name: n, roles: r }) => ({ appId, name: n, roles: r }));

  function submit(e: FormEvent) {
    e.preventDefault();
    if (valid) create.mutate();
  }

  return (
    <Sheet title="Mandant anlegen" onClose={onClose}>
      <form onSubmit={submit} className="form">
        <div className="field">
          <label className="field__label" htmlFor="t-name">Name des Betriebs</label>
          <input id="t-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="t-contact">Kontakt-E-Mail (optional)</label>
          <input id="t-contact" type="email" className="input" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
        </div>
        <fieldset className="role-editor">
          <legend className="field__label">Freigeschaltete Tools</legend>
          {catalog.map((c) => (
            <label key={c.appId} className="check">
              <input
                type="checkbox"
                checked={apps.includes(c.appId)}
                onChange={() => {
                  const next = apps.includes(c.appId) ? apps.filter((a) => a !== c.appId) : [...apps, c.appId];
                  setApps(next);
                  setRoles((r) => r.filter((x) => x === 'platform:tenantAdmin' || next.includes(x.split(':')[0]!)));
                }}
              />
              <span>
                <strong>{c.name}</strong>
                <span className="muted">{c.description}</span>
              </span>
            </label>
          ))}
          {catalog.length === 0 && <p className="note">Der Katalog ist leer – zuerst unter „Tool-Katalog“ ein Tool eintragen.</p>}
        </fieldset>
        <h3 className="form__section">Admin des Betriebs</h3>
        <div className="field">
          <label className="field__label" htmlFor="t-admin-name">Name</label>
          <input id="t-admin-name" className="input" value={adminName} onChange={(e) => setAdminName(e.target.value)} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="t-admin-email">E-Mail</label>
          <input id="t-admin-email" type="email" className="input" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} />
          <span className="field__hint">Bekommt die Einladung und kann danach das Team selbst verwalten.</span>
        </div>
        <RoleEditor apps={assignable} value={roles} onChange={(r) => setRoles([...new Set(['platform:tenantAdmin', ...r])])} idPrefix="t" />
        {create.error && <div className="alert alert--error">{(create.error as ApiError).message}</div>}
        <div className="grid-2">
          <button type="button" className="btn btn--ghost" onClick={onClose}>Abbrechen</button>
          <button type="submit" className="btn btn--primary" disabled={!valid || create.isPending}>Anlegen</button>
        </div>
      </form>
    </Sheet>
  );
}
